import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { defineMessages } from 'react-intl.macro';
import { useFragment } from 'react-relay/hooks';

import { graphql } from 'babel-plugin-relay/macro';

import { Button, Dialog, DialogActions, DialogContent, DialogProps, DialogTitle } from 'components/v2';
import useReduxMutation from 'hooks/useReduxMutation';
import { actionMessageTemplateAuditRestore } from 'sagas/sagaWatchersMessageTemplate';

import { DialogMessageTemplateAuditRestore_MessageTemplate$key } from './__generated__/DialogMessageTemplateAuditRestore_MessageTemplate.graphql';
import { DialogMessageTemplateAuditRestore_Viewer$key } from './__generated__/DialogMessageTemplateAuditRestore_Viewer.graphql';
import { Audit } from './types/auditType';

const messages = defineMessages({
  title: {
    id: 'deprecated.fragments.DialogMessageTemplateAuditRestore.title',
    defaultMessage: 'Restore this version?',
  },
  content: {
    id: 'deprecated.fragments.DialogMessageTemplateAuditRestore.content',
    defaultMessage: 'Are you sure you want to override the current version?',
  },
  actionCancel: {
    id: 'deprecated.fragments.DialogMessageTemplateAuditRestore.actionCancel',
    defaultMessage: 'No, do nothing',
  },
  actionSubmit: {
    id: 'deprecated.fragments.DialogMessageTemplateAuditRestore.actionSubmit',
    defaultMessage: 'Yes, restore this version',
  },
});

export interface DialogMessageTemplateAuditRestoreProps extends Omit<DialogProps, 'classes' | 'onClose'> {
  fragmentRefViewer: DialogMessageTemplateAuditRestore_Viewer$key;
  fragmentRef: DialogMessageTemplateAuditRestore_MessageTemplate$key;
  audit: Audit;
  onClose: (success?: boolean) => void;
}

const DialogMessageTemplateAuditRestore: React.FC<DialogMessageTemplateAuditRestoreProps> = ({
  fragmentRefViewer,
  fragmentRef,
  audit,
  onClose,
  ...rest
}) => {
  const viewer = useFragment<DialogMessageTemplateAuditRestore_Viewer$key>(
    graphql`
      fragment DialogMessageTemplateAuditRestore_Viewer on User {
        _id
      }
    `,
    fragmentRefViewer,
  );

  const messageTemplate = useFragment<DialogMessageTemplateAuditRestore_MessageTemplate$key>(
    graphql`
      fragment DialogMessageTemplateAuditRestore_MessageTemplate on MessageTemplate {
        key
        language
      }
    `,
    fragmentRef,
  );

  const { formatMessage } = useIntl();
  const [execute, isPending] = useReduxMutation(actionMessageTemplateAuditRestore);

  const handleSubmit = useCallback(() => {
    execute({
      method: 'post',
      url: `/messageTemplates/${messageTemplate.key}`,
      data: {
        lang: messageTemplate.language,
        user: viewer._id,
        template: audit.new || '',
        versionDescription: `Restore "${audit.description || `#${audit._id}`}"`,
      },
      callbackSuccess: () => {
        onClose(true);
      },
    });
  }, [onClose, execute, messageTemplate, viewer, audit]);

  return (
    <Dialog fullWidth onClose={() => onClose()} {...rest}>
      <DialogTitle>{formatMessage(messages.title)}</DialogTitle>
      <DialogContent>{formatMessage(messages.content)}</DialogContent>
      <DialogActions disableActionSpacing>
        <Button type="button" variant="text" size="large" color="primary" onClick={() => onClose()}>
          {formatMessage(messages.actionCancel)}
        </Button>
        <Button variant="contained" color="primary" onClick={() => handleSubmit()} disabled={isPending}>
          {formatMessage(messages.actionSubmit)}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DialogMessageTemplateAuditRestore;
